import { Space } from '@gr-monorepo/common/ui';
import Portal from 'features/shared/Portal';
import React, { useContext, useState } from 'react';
import {
  Screen,
  WhiteScreen,
  Step1,
  Step2,
  Step3,
  Content,
  PlateMainScreen,
  ContentMainScreen,
  NewPlate,
  Scroll,
} from './ActiveSchoolListStyle';
import { Popup, PopupWithButton } from './Popup';
import { Title, Text, Header, Button } from './PopupStyled';
import useImperativeDisableScroll from '@gr-monorepo/common/utils/useImperativeDisableScroll';
import { OnboardingContext } from '../OnboardingContext';
import { scrollToAncher } from 'features/shared/AnchorScroll';
import { useClickStreamContext } from 'features/shared/ClickStream';
import useUserData from 'hooks/useUserData';
import { PopupsApi } from 'api';
type Props = {
  nextStep: () => void;
  prevStep?: () => void;
  close: () => void;
};
const ActiveSchoolListScreen0 = (props: Props) => {
  return (
    <Content>
      <Popup close={props.close} style={{ position: 'relative' }}>
        <ContentMainScreen>
          <Header style={{ margin: '0 auto' }}>Здравствуйте!</Header>
          <Space size={16} axis="vertical" />

          <Text>Личный кабинет региона и муниципалитета стал удобнее. Показываем, что изменилось.</Text>
          <Space size={32} axis="vertical" />

          <PlateMainScreen>
            <NewPlate />
            Изменение количества учеников в «Списке активных школ»
          </PlateMainScreen>
          <Space size={32} axis="vertical" />

          <Button style={{ margin: '0 auto' }} onClick={props.nextStep}>
            Ознакомиться в 3 шага
          </Button>
        </ContentMainScreen>
      </Popup>
    </Content>
  );
};
const ActiveSchoolListScreen1 = (props: Props) => {
  return (
    <Content style={{ marginBottom: -60, marginRight: -280 }}>
      <Step1 />
      <PopupWithButton
        {...props}
        buttonNextTitle="Дальше"
        stepCount={3}
        currenStepIndex={0}
        style={{ top: -60, left: 0 }}
      >
        <div style={{ width: 512 }}>
          <Title>Изменяйте количество учеников</Title>
          <Space size={16} axis="vertical" />
          <Text>
            Для изменения количества учеников
            <br /> в конкретной школе муниципалитета нажимайте <br />
            <span style={{ fontWeight: 'bold', fontSize: '24' }}>+</span> в колонке{' '}
            <span style={{ fontWeight: 'bold', fontSize: '24' }}>Всего</span>.
          </Text>
          <Space size={12} axis="vertical" />
          <Text>
            С помощью этих данных вы будете получать <br /> более точную статистику по вашему муниципалитету и всему
            региону.
          </Text>
          <Space size={32} axis="vertical" />
        </div>
      </PopupWithButton>
    </Content>
  );
};
const ActiveSchoolListScreen2 = (props: Props) => {
  return (
    <Content style={{ marginBottom: -60, marginRight: -360 }}>
      <Step2 />
      <PopupWithButton
        {...props}
        buttonNextTitle="Дальше"
        buttonPrevTitle="Назад"
        stepCount={3}
        currenStepIndex={1}
        style={{ top: 100, left: -508 }}
      >
        <div style={{ width: 452 }}>
          <Title>Изменяйте количество учеников</Title>
          <Space size={16} axis="vertical" />
          <Text>
            Введите количество учеников
            <br />
            в появившемся окне и нажмите <span style={{ fontWeight: 'bold' }}>Сохранить.</span>
          </Text>
          <Space size={32} axis="vertical" />
        </div>
      </PopupWithButton>
    </Content>
  );
};
const ActiveSchoolListScreen3 = (props: Props) => {
  return (
    <Content style={{ marginBottom: -60, marginRight: -280 }}>
      <Step3 />
      <PopupWithButton
        {...props}
        buttonNextTitle="ЗАВЕРШИТЬ"
        buttonPrevTitle="Назад"
        stepCount={3}
        currenStepIndex={2}
        style={{ top: 17, left: -15 }}
      >
        <div style={{ width: 512 }}>
          <Title>Изменяйте количество учеников</Title>
          <Space size={16} axis="vertical" />
          <Text>Обновленные данные появятся в таблице.</Text>
          <Space size={12} axis="vertical" />
          <Text>При необходимости количество учеников можно изменить снова.</Text>
          <Space size={32} axis="vertical" />
        </div>
      </PopupWithButton>
    </Content>
  );
};

const screens = [ActiveSchoolListScreen0, ActiveSchoolListScreen1, ActiveSchoolListScreen2, ActiveSchoolListScreen3];

const ActiveSchoolList = () => {
  const userData = useUserData();
  const context = useContext(OnboardingContext);
  const showOnboarding = context.onboardingName === 'activeSchoolList';
  const [screenIndex, setScreenIndex] = useState(0);
  const CurrentScreen: ((props: Props) => JSX.Element) | undefined = screens[screenIndex];
  const { logPageEvent } = useClickStreamContext();
  useImperativeDisableScroll({ element: document.body, disabled: showOnboarding });
  const close = () => {
    if (screenIndex > 0) scrollToAncher('school_active_school');
    context.saveLog();
    PopupsApi.loggedShowPopup(51, { subjectId: userData?.id || 0 });
  };
  const closeClick = () => {
    logPageEvent({
      eventType: 'onboarding_active_school_click_close',
      eventInfo: `Онбординг|Список активных школ: Клик по кнопке закрыть на шаге ${screenIndex}`,
    });
    close();
  };
  const nextStep = () => {
    logPageEvent({
      eventType: 'onboarding_active_school_click_next',
      eventInfo: `Онбординг|Список активных школ: Клик по кнопке дальше на шаге ${screenIndex}`,
    });
    screenIndex < 3 ? setScreenIndex(screenIndex + 1) : close();
  };
  const prevStep = () => {
    logPageEvent({
      eventType: 'onboarding_active_school_click_prev',
      eventInfo: `Онбординг|Список активных школ: Клик по кнопке назад на шаге ${screenIndex}`,
    });
    setScreenIndex(screenIndex - 1);
  };
  const overflowClick = () => {
    logPageEvent({
      eventType: 'onboarding_active_school_click_overflow',
      eventInfo: `Онбординг|Список активных школ: Клик вне попапап на шаге ${screenIndex}`,
    });
    screenIndex < 3 ? setScreenIndex(screenIndex + 1) : close();
  };

  if (!showOnboarding) return <></>;
  return (
    <Portal>
      <Screen onClick={overflowClick}>
        <WhiteScreen></WhiteScreen>
        <Scroll>
          {CurrentScreen && (
            <CurrentScreen nextStep={nextStep} prevStep={screenIndex > 1 ? prevStep : undefined} close={closeClick} />
          )}
        </Scroll>
      </Screen>
    </Portal>
  );
};
export default ActiveSchoolList;
